/**
 * The Connectors page's state: the list, a connect flow that opens the
 * Connect Link and watches until the app shows up connected, and disconnect.
 *
 * Connecting happens in another tab (the provider's OAuth), so there is no
 * event to await here: `useConnectionWatch` (shared with the agent's connect
 * card) polls the pending app's status until it is `connected`, or gives up
 * after `POLL_MAX_MS`. Landing back via the Connect Link's callback
 * (`?connected=<slug>`) also triggers a refresh, so whichever happens first
 * settles the card.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ConnectorsAPI } from "../api/connectorsApi";
import { connectorsCallbackUrl } from "../routes";
import type { Connector } from "../types";
import {
  POLL_INTERVAL_MS,
  POLL_MAX_MS,
  useConnectionWatch
} from "./useConnectionWatch";

export { POLL_INTERVAL_MS, POLL_MAX_MS };

const NO_STATUSES = {};

export type ConnectorsState = {
  items: Connector[];
  enabled: boolean;
  loading: boolean;
  error: string | null;
  /** The app whose Connect Link is open right now, if any. */
  pendingSlug: string | null;
  /** The app being disconnected right now, if any. */
  disconnectingSlug: string | null;
  refresh: () => Promise<void>;
  connect: (slug: string) => Promise<void>;
  disconnect: (slug: string) => Promise<void>;
};

const messageOf = (error: unknown, fallback: string): string => {
  if (typeof error === "object" && error !== null) {
    const data = (error as { response?: { data?: { error?: unknown } } })
      .response?.data;
    if (data && typeof data.error === "string") return data.error;
    const { message } = error as { message?: unknown };
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

export function useConnectors(): ConnectorsState {
  const [items, setItems] = useState<Connector[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  const [disconnectingSlug, setDisconnectingSlug] = useState<string | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const mounted = useRef(true);
  // The pending app's name, for the toast once the watch sees it connected.
  const pendingName = useRef<string>("");

  const load = useCallback(async (): Promise<Connector[]> => {
    const data = await ConnectorsAPI.list();
    if (mounted.current) {
      setItems(data.items);
      setEnabled(data.enabled);
      setError(null);
    }
    return data.items;
  }, []);

  const refresh = useCallback(async () => {
    try {
      await load();
    } catch (e) {
      if (mounted.current) setError(messageOf(e, "Could not load connectors"));
    }
  }, [load]);

  const watch = useConnectionWatch(NO_STATUSES, {
    onAllConnected: () => {
      if (!mounted.current) return;
      setPendingSlug(null);
      toast.success(`${pendingName.current} connected`, {
        description: "Your agents can use it from now on."
      });
      void refresh();
    },
    onTimeout: () => {
      if (mounted.current) setPendingSlug(null);
    }
  });
  const { watch: watchSlug, stop: stopPolling } = watch;

  const connect = useCallback(
    async (slug: string) => {
      const target = items.find((c) => c.slug === slug);
      const name = target?.name ?? slug;
      try {
        setPendingSlug(slug);
        const { redirect_url } = await ConnectorsAPI.connect(
          slug,
          connectorsCallbackUrl(window.location.origin, slug)
        );
        if (!redirect_url) throw new Error("No Connect Link was returned");
        // The click's activation still covers this open (the API round trip
        // is well inside the browser's window); if a blocker refuses anyway,
        // offer the link in a toast the user can click.
        const opened = window.open(
          redirect_url,
          "_blank",
          "noopener,noreferrer"
        );
        if (!opened) {
          toast.info(`Open the ${name} sign-in to continue`, {
            duration: 60_000,
            action: {
              label: "Open",
              onClick: () =>
                window.open(redirect_url, "_blank", "noopener,noreferrer")
            }
          });
        }
        pendingName.current = name;
        watchSlug(slug);
      } catch (e) {
        setPendingSlug(null);
        toast.error(`Could not start connecting ${name}`, {
          description: messageOf(e, "Please try again.")
        });
      }
    },
    [items, watchSlug]
  );

  const disconnect = useCallback(
    async (slug: string) => {
      const target = items.find((c) => c.slug === slug);
      const name = target?.name ?? slug;
      setDisconnectingSlug(slug);
      try {
        await ConnectorsAPI.disconnect(slug);
        await load();
        toast.success(`${name} disconnected`);
      } catch (e) {
        toast.error(`Could not disconnect ${name}`, {
          description: messageOf(e, "Please try again.")
        });
      } finally {
        if (mounted.current) setDisconnectingSlug(null);
      }
    },
    [items, load]
  );

  // Initial load.
  useEffect(() => {
    mounted.current = true;
    setLoading(true);
    refresh().finally(() => {
      if (mounted.current) setLoading(false);
    });
    return () => {
      mounted.current = false;
      stopPolling();
    };
  }, [refresh, stopPolling]);

  // Landing from a Connect Link: `?connected=<slug>&status=success|failed`.
  useEffect(() => {
    const slug = searchParams.get("connected");
    if (!slug) return;
    const status = searchParams.get("status");
    const next = new URLSearchParams(searchParams);
    ["connected", "status", "connected_account_id", "error"].forEach((k) =>
      next.delete(k)
    );
    setSearchParams(next, { replace: true });
    if (status === "failed") {
      toast.error("Connection was not completed", {
        description: searchParams.get("error") ?? "You can try again."
      });
      return;
    }
    refresh().then(() => {
      stopPolling();
      setPendingSlug(null);
    });
  }, [searchParams, setSearchParams, refresh, stopPolling]);

  return {
    items,
    enabled,
    loading,
    error,
    pendingSlug,
    disconnectingSlug,
    refresh,
    connect,
    disconnect
  };
}
